import type { Container } from "pixi.js";
import type { ComputedGridLayout, GridLayout, GridPoint, PresentationBarrier, PresentationTaskRunner, SymbolView, TweenEasing } from "@slot/presentation";

/**
 * INTERNAL — the pre-spin drop-out: the outgoing symbols fall down off the grid, column by column
 * left to right, bottom row first, under the `prespin_motion` scope. Releases the spin barrier
 * `releaseDelaySeconds` after the LAST symbol has started falling (0: the reveal may prepare while the tail is
 * still dropping), at the latest when the drop-out ends. Runner-only (engine time); with no runner the symbols
 * are moved off-grid instantly.
 */
export class GridPreSpinDrop {
  public static readonly SCOPE = "prespin_motion";

  public async dropOutDownAndRecycle(input: {
    symbolsByIndex: Map<number, SymbolView>;
    layoutUtil: GridLayout;
    layout: ComputedGridLayout;

    gridColumnCount: number;
    gridRowCount: number;

    runner: PresentationTaskRunner | null;

    dropDistanceRows?: number;
    durationSeconds?: number;
    columnStaggerSeconds?: number;
    withinColumnStaggerSeconds?: number;
    easing?: TweenEasing;

    barrier?: PresentationBarrier | null;
    barrierKey?: string;
    /** How long after the last symbol starts to fall the barrier is released (capped at the drop-out's end). */
    releaseDelaySeconds: number;
  }): Promise<void> {
    const runner = input.runner;
    const scope = GridPreSpinDrop.SCOPE;

    const duration = input.durationSeconds ?? 0.3;
    const dropDistanceRows = input.dropDistanceRows ?? 7;
    const columnStaggerSeconds = input.columnStaggerSeconds ?? 0.035;
    const withinColumnStaggerSeconds = input.withinColumnStaggerSeconds ?? 0.012;
    // Falling out: a weighted-acceleration feel.
    const easing: TweenEasing = input.easing ?? "cubicIn";

    let gateReleased = false;
    const releaseGate = (): void => {
      if (gateReleased) return;
      gateReleased = true;
      if (input.barrier && input.barrierKey) input.barrier.release(input.barrierKey);
    };

    const anyVisible = Array.from(input.symbolsByIndex.values()).some((v) => v?.node?.visible);
    if (!anyVisible) {
      releaseGate();
      return;
    }

    type DropTask = { node: Container; endPos: GridPoint; startAt: number };
    const dropTasks: DropTask[] = [];
    let baseStart = 0;
    for (let col = 0; col < input.gridColumnCount; col++) {
      if (col !== 0) baseStart += columnStaggerSeconds;
      // Deterministic: bottom → top.
      const columnViews = input.layoutUtil.getColumnSymbols({
        symbolsByIndex: input.symbolsByIndex,
        cols: input.gridColumnCount,
        rows: input.gridRowCount,
        col,
        order: "bottomToTop",
        requireActive: true,
      });
      for (let i = 0; i < columnViews.length; i++) {
        const node = columnViews[i].node;
        // Down off the grid (Pixi y-down).
        const endPos = { x: node.position.x, y: node.position.y + input.layout.cellHeight * dropDistanceRows };
        dropTasks.push({ node, endPos, startAt: baseStart + i * withinColumnStaggerSeconds });
      }
    }

    if (!runner) {
      for (const t of dropTasks) t.node.position.set(t.endPos.x, t.endPos.y);
      releaseGate();
      return;
    }

    const lastStartDelaySeconds = (input.gridColumnCount - 1) * columnStaggerSeconds + (input.gridRowCount - 1) * withinColumnStaggerSeconds;

    const run = async (): Promise<void> => {
      const promises: Promise<void>[] = [];
      for (const t of dropTasks) {
        promises.push(
          (async () => {
            if (t.startAt > 0) await runner.sleep(t.startAt, scope);
            await runner.tweenTo(t.node, duration, { position: { x: t.endPos.x, y: t.endPos.y } }, scope, { easing });
          })(),
        );
      }
      // Every symbol falls for `duration`, so the last one's start plus `duration` is the drop-out's end.
      const releaseAt = lastStartDelaySeconds + Math.min(Math.max(0, input.releaseDelaySeconds), duration);
      promises.push(
        (async () => {
          if (releaseAt > 0) await runner.sleep(releaseAt, scope);
          releaseGate();
        })(),
      );
      await Promise.all(promises);
    };

    await runner.runInScope(scope, run);
    releaseGate();
  }
}
